import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import { OnboardingTrophyRing } from './OnboardingTrophyRing';
import { OnboardingBubbleContent } from './OnboardingBubbleContent';
import { CHAPTER_ZERO_OVERLAY_Z_INDEX } from './ChapterZeroAchievementToast';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from './ui/sidebar';
import { cn } from '../lib/utils';

const CELEBRATE_PORTAL_MS = 2400;

export const OnboardingTrophy: React.FC = () => {
  const { t } = useLocale();
  const { progress, loading, celebrationKey } = useOnboardingProgress();
  const { registerMobileSidebarCloser, openMobileBubble, mobileBubbleOpen } = useOnboarding();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [celebrateRect, setCelebrateRect] = useState<DOMRect | null>(null);
  const ringAnchorRef = useRef<HTMLSpanElement>(null);
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed';
  const iconActive = hovered || open || (isMobile && mobileBubbleOpen);
  const ringSize = isCollapsed ? 32 : 38;
  const isCelebrating = celebrateRect !== null;

  useEffect(() => {
    if (!isMobile) {
      registerMobileSidebarCloser(null);
      return;
    }

    registerMobileSidebarCloser(() => setOpenMobile(false));
    return () => registerMobileSidebarCloser(null);
  }, [isMobile, registerMobileSidebarCloser, setOpenMobile]);

  const points = progress?.points ?? 0;
  const targetPoints =
    progress?.nextMilestonePoints ??
    progress?.finalMilestonePoints ??
    0;
  const resolvedTarget = targetPoints > 0 ? targetPoints : Math.max(points, 1);

  const pathTooltip = useMemo(() => {
    if (!progress) {
      return t('onboarding_title');
    }

    const pointsLine = t('onboarding_points_summary')
      .replace('{points}', String(progress.points))
      .replace('{target}', String(progress.finalMilestonePoints));
    const nextLine =
      progress.nextMilestonePoints === null
        ? t('onboarding_roadmap_complete')
        : t('onboarding_points_to_next').replace(
            '{points}',
            String(Math.max(progress.nextMilestonePoints - progress.points, 0))
          );

    return (
      <div className="flex flex-col gap-0.5 text-left">
        <span className="font-medium">{t('onboarding_title')}</span>
        <span>{pointsLine}</span>
        <span>{nextLine}</span>
        <span className="mt-1 opacity-90">{t('onboarding_tooltip_click_more')}</span>
      </div>
    );
  }, [progress, t]);

  useLayoutEffect(() => {
    if (celebrationKey <= 0) {
      setCelebrateRect(null);
      return;
    }

    setCelebrateRect(ringAnchorRef.current?.getBoundingClientRect() ?? null);
    const hideTimer = window.setTimeout(() => setCelebrateRect(null), CELEBRATE_PORTAL_MS);
    return () => window.clearTimeout(hideTimer);
  }, [celebrationKey]);

  const trophyButton = (
    <SidebarMenuButton
      className={cn(
        'w-full [&>span:first-child]:shrink-0',
        isCollapsed && 'justify-center'
      )}
      tooltip={
        isMobile || open
          ? undefined
          : {
              children: pathTooltip,
              className: 'max-w-[240px]',
            }
      }
      aria-label={t('onboarding_title')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={isMobile ? () => openMobileBubble() : undefined}
      type="button"
    >
      <span
        ref={ringAnchorRef}
        className={cn('inline-flex', isCelebrating && 'opacity-0')}
      >
        <OnboardingTrophyRing
          points={points}
          targetPoints={resolvedTarget}
          size={ringSize}
          active={iconActive && !isCelebrating}
          celebrationKey={0}
        />
      </span>
      {!isCollapsed && (
        <div className="flex flex-col items-start flex-1 min-w-0">
          <span className="text-sm font-medium truncate w-full">
            {t('onboarding_title')}
          </span>
          <span className="text-xs text-muted-foreground truncate w-full">
            {loading
              ? t('loading')
              : t('onboarding_points_short').replace('{points}', String(points))}
          </span>
        </div>
      )}
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {isMobile ? (
          trophyButton
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trophyButton}</PopoverTrigger>
            <PopoverContent
              side="right"
              align="end"
              sideOffset={8}
              className={cn(
                'w-auto max-w-[min(100vw-2rem,24rem)] p-4 z-[10050]',
                'relative before:absolute before:left-[-6px] before:bottom-4',
                'before:h-3 before:w-3 before:rotate-45 before:border-l before:border-b',
                'before:border-border before:bg-popover'
              )}
            >
              {progress ? (
                <OnboardingBubbleContent progress={progress} />
              ) : (
                <p className="text-xs text-muted-foreground">{t('loading')}</p>
              )}
            </PopoverContent>
          </Popover>
        )}
      </SidebarMenuItem>
      {celebrateRect &&
        createPortal(
          <div
            className="pointer-events-none"
            style={{
              position: 'fixed',
              left: celebrateRect.left,
              top: celebrateRect.top,
              width: celebrateRect.width,
              height: celebrateRect.height,
              zIndex: CHAPTER_ZERO_OVERLAY_Z_INDEX,
            }}
          >
            <OnboardingTrophyRing
              points={points}
              targetPoints={resolvedTarget}
              size={ringSize}
              active={false}
              celebrationKey={celebrationKey}
            />
          </div>,
          document.body
        )}
    </SidebarMenu>
  );
};
